import React from 'react';
import { BackgroundPreset } from '../types';

interface BackgroundGridProps {
    backgrounds: BackgroundPreset[];
    loading: boolean;
    selectedId: string | null;
    onSelect: (bgId: string, url: string | null) => void;
    categories: { value: string; label: string }[];
    currentCategory: string;
    onCategoryChange: (cat: string) => void;
    showNoChangeOption?: boolean;
}

export const BackgroundGrid: React.FC<BackgroundGridProps> = ({
    backgrounds,
    loading,
    selectedId,
    onSelect,
    categories,
    currentCategory,
    onCategoryChange,
    showNoChangeOption = true,
}) => {
    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Backgrounds Gallery</h3>
                    <p className="text-sm text-gray-500">Select a background for your model</p>
                </div>
                <div className="flex bg-gray-100 rounded-full p-1 overflow-x-auto scrollbar-hide">
                    {categories.map((cat) => (
                        <button
                            key={cat.value}
                            onClick={() => onCategoryChange(cat.value)}
                            className={`px-3 sm:px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap ${currentCategory === cat.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {cat.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading && (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
                </div>
            )}

            {!loading && (
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                    {showNoChangeOption && (
                        <button
                            onClick={() => onSelect('none', null)}
                            className={`group relative text-left transition-all ${selectedId === 'none' ? 'scale-[0.98]' : 'hover:scale-[1.02]'}`}
                        >
                            <div
                                className={`relative aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden border-2 flex flex-col items-center justify-center bg-gray-50 ${selectedId === 'none' ? 'ring-4 ring-secondary-500 border-secondary-500' : 'border-dashed border-gray-300'
                                    }`}
                            >
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mb-3 shadow-sm">
                                    <i className="fas fa-ban text-gray-400 text-xl"></i>
                                </div>
                                <p className="text-sm font-bold text-gray-600">No Change</p>
                                <p className="text-[10px] text-gray-400 mt-1">Keep original bg</p>
                                {selectedId === 'none' && (
                                    <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full p-1.5 shadow-lg">
                                        <i className="fas fa-check text-xs"></i>
                                    </div>
                                )}
                            </div>
                        </button>
                    )}

                    {backgrounds.map((bg) => {
                        const isSelected = selectedId === bg.backgroundId;
                        return (
                            <button
                                key={bg.backgroundId}
                                onClick={() => onSelect(bg.backgroundId, bg.imageUrl)}
                                className={`group relative text-left transition-all ${isSelected ? 'scale-[0.98]' : 'hover:scale-[1.02]'}`}
                            >
                                <div className={`relative aspect-[3/4] rounded-xl sm:rounded-2xl overflow-hidden ${isSelected ? 'ring-4 ring-secondary-500' : ''}`}>
                                    <img
                                        src={bg.imageUrl}
                                        alt={bg.category}
                                        className="w-full h-full object-cover bg-gray-100 group-hover:scale-105 transition-transform duration-300"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x225?text=Background';
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-60" />
                                    <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                                        <p className="text-sm sm:text-base font-semibold text-white">{bg.category}</p>
                                    </div>
                                    {isSelected && (
                                        <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full p-1.5 shadow-lg">
                                            <i className="fas fa-check text-xs"></i>
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
